import * as React from 'react';
import {
  Text,
  Tooltip,
  Link,
  Icon,
  BlockStack,
  InlineStack,
  Card,
  Box,
  Divider,
  UnstyledButton,
} from '@shopify/polaris';
import {TranslatableResourceType} from 'types';
import {useGroupedTranslatableRoutes} from 'utilities/use-grouped-translatable-routes';
import {useI18n} from '@shopify/react-i18n';
import {useUrl} from '@web-utilities/url';
import {ChevronRightIcon, InfoIcon} from '@shopify/polaris-icons';
import {useShopLocalesAndMarkets} from 'utilities/use-shop-locales-and-markets';

import styles from './GroupedResourceList.scss';

interface Props {
  machineTranslationsActive: boolean;
}

const I18N_SCOPE = 'GroupedResourceList';

export function GroupedResourceList({machineTranslationsActive}: Props) {
  const [i18n] = useI18n();
  const urlHelper = useUrl();
  const groupedTranslatableRoutes = useGroupedTranslatableRoutes();
  const {currentLocale} = useShopLocalesAndMarkets();

  const showTooltip = (id: TranslatableResourceType) =>
    [
      TranslatableResourceType.EmailTemplate,
      TranslatableResourceType.OnlineStoreThemeLocaleContent,
      TranslatableResourceType.CookieBanner,
    ].includes(id) && !currentLocale?.primary;

  const renderMachineTranslationTooltip = (title: string) => (
    <div className={styles.MachineTranslationToolTip}>
      <Tooltip
        content={i18n.translate(`tooltips.${title}`, {
          scope: [I18N_SCOPE],
        })}
        preferredPosition="below"
      >
        <Icon source={InfoIcon} tone="base" />
      </Tooltip>
    </div>
  );

  const renderPolicyNotice = () => (
    <Text variant="bodySm" tone="subdued" as="span">
      {i18n.translate(
        'noMachineTranslationForPolicies',
        {
          scope: [I18N_SCOPE],
        },
        {
          learnMoreLink: (
            <Link
              removeUnderline
              target="_blank"
              url={urlHelper.helpCenterUrl({
                locale: i18n.locale,
                path: 'manual/markets/languages/translate-adapt-app#auto-translate',
              })}
            >
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
              <span onClick={(event) => event.stopPropagation()}>
                {i18n.translate('learnMoreLinkLabel', {
                  scope: [I18N_SCOPE],
                })}
              </span>
            </Link>
          ),
        },
      )}
    </Text>
  );

  return (
    <Card padding="0" roundedAbove="sm">
      {groupedTranslatableRoutes.map(({label, items}, idx) => {
        return (
          <React.Fragment key={`resource-item-${label}`}>
            <ResourceGroupHeader showTopBorder={idx !== 0}>
              <Text as="h2" variant="headingSm">
                {label}
              </Text>
            </ResourceGroupHeader>
            {items.map(({resourceType, title, navigate, label}, idx) => (
              <UnstyledButton
                key={`${resourceType}-${title}`}
                onClick={navigate}
                className={styles.ResourceTypeRow}
              >
                <Box padding="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="200">
                      <BlockStack gap="100">
                        <Text as="p" variant="headingSm" fontWeight="regular">
                          {label}
                        </Text>
                        {machineTranslationsActive &&
                          resourceType ===
                            TranslatableResourceType.ShopPolicy &&
                          renderPolicyNotice()}
                      </BlockStack>
                      {showTooltip(resourceType) &&
                        renderMachineTranslationTooltip(title)}
                    </InlineStack>
                    <Icon source={ChevronRightIcon} tone="base" />
                  </InlineStack>
                </Box>
                {idx !== items.length - 1 && <Divider />}
              </UnstyledButton>
            ))}
          </React.Fragment>
        );
      })}
    </Card>
  );
}

export function ResourceGroupHeader({
  children,
  showTopBorder,
}: {
  children: React.ReactNode;
  showTopBorder?: boolean;
}) {
  return (
    <Box
      paddingBlockEnd="300"
      paddingBlockStart="300"
      paddingInlineEnd="400"
      paddingInlineStart="400"
      background="bg-surface-secondary"
      borderBlockEndWidth="025"
      borderBlockStartWidth={showTopBorder ? '025' : undefined}
      borderColor="border-secondary"
    >
      {children}
    </Box>
  );
}
